// jsaForm/core/trainingInfo.actions.js

// Helper to get reset fields for training when hands-on training is unchecked/disabled
const getResetTrainingFields = () => ({
  ewiTrainerName: "",
  ewiTrainerId: "",
  nonEwiTrainerName: "",
  trainingInformation: "",
});

export const makeJsaCoreTrainingInfoActions = ({ bump, emitJsaPatch }) => ({
  updateJsaTrainingCompletedYes(jsaData, value, emitPatch) {
    jsaData.jsaTrainingInfo.updateJobTrainingCompletedYes(value);
    if (emitPatch) {
      const fields = { jobTrainingCompletedYes: value };
      if (value) {
        fields.jobTrainingCompletedNo = false;
      }
      const patch = jsaData.jsaTrainingInfo.toPatch(
        fields,
        jsaData.jsaBasicInfo.rowId
      );
      emitJsaPatch(patch, emitPatch);
    }
    bump();
  },
  updateJsaTrainingCompletedNo(jsaData, value, emitPatch) {
    jsaData.jsaTrainingInfo.updateJobTrainingCompletedNo(value);
    if (emitPatch) {
      const fields = { jobTrainingCompletedNo: value };
      if (value) {
        fields.jobTrainingCompletedYes = false;
      }
      const patch = jsaData.jsaTrainingInfo.toPatch(
        fields,
        jsaData.jsaBasicInfo.rowId
      );
      emitJsaPatch(patch, emitPatch);
    }
    bump();
  },

  updateJsaHandsOnJobTrainingYes(jsaData, value, emitPatch) {
    // Capture employees before reset (for delete patches)
    const employeesToDelete = !value
      ? [...jsaData.jsaTrainingInfo.jsaTrainingEmployeesInfo]
      : [];

    jsaData.jsaTrainingInfo.updateHandsOnJobTrainingYes(value);

    const fields = { handsOnJobTrainingYes: value };
    if (value) {
      fields.handsOnJobTrainingNo = false;
    } else {
      Object.assign(fields, getResetTrainingFields());
    }

    if (emitPatch) {
      // Send delete patches for each training employee
      employeesToDelete.forEach((emp) => {
        const deletePatch = emp.toPatch(
          {},
          jsaData.jsaBasicInfo.rowId,
          "remove"
        );
        emitJsaPatch(deletePatch, emitPatch);
      });
      // Send update patch for scalar fields
      const patch = jsaData.jsaTrainingInfo.toPatch(
        fields,
        jsaData.jsaBasicInfo.rowId
      );
      emitJsaPatch(patch, emitPatch);
    }
    // No need to set showTrainingInfo - component derives it from data
    bump();
  },
  updateJsaHandsOnJobTrainingNo(jsaData, value, emitPatch) {
    // Capture employees before reset (for delete patches)
    const employeesToDelete = value
      ? [...jsaData.jsaTrainingInfo.jsaTrainingEmployeesInfo]
      : [];

    jsaData.jsaTrainingInfo.updateHandsOnJobTrainingNo(value);

    const fields = { handsOnJobTrainingNo: value };
    if (value) {
      fields.handsOnJobTrainingYes = false;
      Object.assign(fields, getResetTrainingFields());
    }

    if (emitPatch) {
      // Send delete patches for each training employee
      employeesToDelete.forEach((emp) => {
        const deletePatch = emp.toPatch(
          {},
          jsaData.jsaBasicInfo.rowId,
          "remove"
        );
        emitJsaPatch(deletePatch, emitPatch);
      });
      // Send update patch for scalar fields
      const patch = jsaData.jsaTrainingInfo.toPatch(
        fields,
        jsaData.jsaBasicInfo.rowId
      );
      emitJsaPatch(patch, emitPatch);
    }
    // No need to set showTrainingInfo - component derives it from data
    bump();
  },

  updateJsaEwiTrainerYes(jsaData, value, emitPatch) {
    jsaData.jsaTrainingInfo.updateEwiTrainerYes(value);
    if (emitPatch) {
      // ewiTrainerYes/No are derived fields (not in DB), only patch actual fields that are cleared
      const fields = {};
      if (value) {
        // Selecting EWI trainer clears non-EWI trainer name
        fields.nonEwiTrainerName = "";
      } else {
        // Deselecting clears EWI trainer info
        fields.ewiTrainerName = "";
        fields.ewiTrainerId = "";
      }
      if (Object.keys(fields).length > 0) {
        const patch = jsaData.jsaTrainingInfo.toPatch(
          fields,
          jsaData.jsaBasicInfo.rowId
        );
        emitJsaPatch(patch, emitPatch);
      }
    }
    // No need to set showEwiTrainerInput - component derives it from data
    bump();
  },

  updateJsaEwiTrainerNo(jsaData, value, emitPatch) {
    jsaData.jsaTrainingInfo.updateEwiTrainerNo(value);
    if (emitPatch) {
      // ewiTrainerYes/No are derived fields (not in DB), only patch actual fields that are cleared
      if (value) {
        // Selecting non-EWI trainer clears EWI trainer info
        const fields = {
          ewiTrainerName: "",
          ewiTrainerId: "",
        };
        const patch = jsaData.jsaTrainingInfo.toPatch(
          fields,
          jsaData.jsaBasicInfo.rowId
        );
        emitJsaPatch(patch, emitPatch);
      }
      // If value=false, nothing is cleared, so no patch needed
    }
    // No need to set showEwiTrainerInput - component derives it from data
    bump();
  },

  updateJsaEwiTrainerInfo(jsaData, value, emitPatch) {
    jsaData.jsaTrainingInfo.updateEwiTrainerInfo(value);
    if (emitPatch) {
      // Patch the actual DB fields, not the composite 'ewiTrainerInfo'
      const fields = value
        ? { ewiTrainerName: value.label, ewiTrainerId: value.value }
        : { ewiTrainerName: "", ewiTrainerId: "" };
      const patch = jsaData.jsaTrainingInfo.toPatch(
        fields,
        jsaData.jsaBasicInfo.rowId
      );
      emitJsaPatch(patch, emitPatch);
    }
    bump();
  },

  updateJsaNonEwiTrainerName(jsaData, value, emitPatch) {
    jsaData.jsaTrainingInfo.updateNonEwiTrainerName(value);
    if (emitPatch) {
      const patch = jsaData.jsaTrainingInfo.toPatch(
        { nonEwiTrainerName: value },
        jsaData.jsaBasicInfo.rowId
      );
      emitJsaPatch(patch, emitPatch);
    }
    bump();
  },

  updateJsaTrainingInformation(jsaData, value, emitPatch) {
    jsaData.jsaTrainingInfo.updateTrainingInformation(value);
    if (emitPatch) {
      const patch = jsaData.jsaTrainingInfo.toPatch(
        { trainingInformation: value },
        jsaData.jsaBasicInfo.rowId
      );
      emitJsaPatch(patch, emitPatch);
    }
    bump();
  },

  updateJsaTrainingAutoAddEmployees(jsaData, employees, emitPatch) {
    const addedRows =
      jsaData.jsaTrainingInfo.addDelEmployeesToJsaTrainingEmployeesInfo(
        employees
      );
    // Emit individual patches for each added employee
    if (emitPatch && addedRows && addedRows.length > 0) {
      for (const row of addedRows) {
        const patch = row.toPatch(
          { employeeId: row.employeeId },
          jsaData.jsaBasicInfo.rowId,
          "add"
        );
        emitJsaPatch(patch, emitPatch);
      }
    }
    bump();
  },

  updateJsaTrainingAddBlankEmployee(jsaData, emitPatch) {
    const newRow = jsaData.jsaTrainingInfo.addBlankJsaTrainingEmployeesInfo();
    if (emitPatch && newRow) {
      const patch = newRow.toPatch(
        { employeeId: null },
        jsaData.jsaBasicInfo.rowId,
        "add"
      );
      emitJsaPatch(patch, emitPatch);
    }
    bump();
  },

  updateJsaTrainingRemoveEmployee(jsaData, employeeRowId, emitPatch) {
    const row = jsaData.jsaTrainingInfo.jsaTrainingEmployeesInfo.find(
      (r) => r.rowId === employeeRowId
    );
    jsaData.jsaTrainingInfo.removeJsaTrainingEmployeesInfoRow(employeeRowId);
    if (emitPatch && row) {
      const patch = row.toPatch({}, jsaData.jsaBasicInfo.rowId, "remove");
      emitJsaPatch(patch, emitPatch);
    }
    bump();
  },
  updateJsaTrainingEmployeeInfo(jsaData, employee, emitPatch) {
    const row =
      jsaData.jsaTrainingInfo.updateJsaTrainingEmployeesInfoRow(employee);
    if (emitPatch && row) {
      const patch = row.toPatch(
        { employeeId: employee.employeeId },
        jsaData.jsaBasicInfo.rowId
      );
      emitJsaPatch(patch, emitPatch);
    }
    bump();
  },
});
